import { useState, type FormEvent } from 'react';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db8gmuk6oi3g3tv8un4g';

export default function AboutPage() {
  useDocumentMeta({
    title: '关于我们 - 红桃视频',
    description:
      '了解红桃视频的定位、内容范围与团队理念。红桃视频提供电影、电视剧、综艺等多类别高清视频内容，并欢迎你的反馈与建议。',
    keywords: '关于红桃视频,红桃视频介绍,联系我们,意见反馈,高清视频平台',
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const name = String(formData.get('name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();

    if (!name || !email || !message) {
      setStatus('error');
      setErrorMessage('请完整填写称呼、邮箱和反馈内容后再提交。');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? null) as Record<string, unknown> | null;
      const serverMessage =
        String(meta?.message ?? parsed?.message ?? meta?.detail ?? responseText ?? '');
      const code = String(parsed?.code ?? '');
      const isSpam = serverMessage.toLowerCase().includes('spam');

      if (!response.ok || code !== 'OK' || isSpam) {
        setStatus('error');
        setErrorMessage(
          serverMessage && !isSpam ? serverMessage : '提交失败，请稍后重试，或直接发送邮件联系我们。',
        );
        return;
      }

      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
      setErrorMessage('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <section className="relative rounded-xl overflow-hidden border border-background-200/70">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20cinematic%20abstract%20background%20of%20a%20dark%20red%20and%20amber%20gradient%20light%20with%20subtle%20film%20grain%20and%20soft%20bokeh%20particles%2C%20moody%20modern%20streaming%20platform%20banner%2C%20high%20detail&width=1600&height=700&seq=ht-about-hero&orientation=landscape"
          alt="红桃视频品牌氛围图"
          title="红桃视频 高清在线观看平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/60" />
        <div className="relative p-6 md:p-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/15 text-primary-300 text-xs font-semibold">
            <i className="ri-heart-3-fill" />
            关于我们
          </span>
          <h1 className="mt-4 font-heading text-3xl md:text-4xl font-extrabold text-foreground-950">
            关于红桃视频
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-foreground-700 leading-relaxed">
            红桃视频是一个以高清视频在线观看为核心的内容平台，汇集电影、电视剧、综艺等多类别影视内容。我们希望用清晰的分类、便捷的搜索和贴心的推荐，让每一位观众都能在最短的时间里，找到最想看的那一部作品，享受轻松愉悦的观影过程。
          </p>
        </div>
      </section>

      <section className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
        {[
          {
            icon: 'ri-focus-3-line',
            title: '我们的宗旨',
            desc: '把复杂的片单变简单。用结构化的分类与筛选，帮助用户快速定位感兴趣的视频内容。',
          },
          {
            icon: 'ri-stack-line',
            title: '内容范围',
            desc: '覆盖电影、电视剧、综艺三大类别，包含悬疑、科幻、动作、都市、音乐等丰富题材。',
          },
          {
            icon: 'ri-heart-3-line',
            title: '团队理念',
            desc: '我们相信好的内容值得被认真呈现，持续打磨每一个细节，只为让观看体验更顺畅。',
          },
        ].map((item) => (
          <div
            key={item.title}
            className="rounded-lg bg-background-100 border border-background-200/70 p-6"
          >
            <span className="w-11 h-11 rounded-lg bg-primary-500/12 flex items-center justify-center text-primary-400">
              <i className={`${item.icon} text-xl`} />
            </span>
            <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h2>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </section>

      <section className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">联系我们</h2>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
            如果你对平台有任何建议、内容反馈或合作意向，欢迎通过以下方式与我们取得联系。
          </p>

          <div className="mt-6 flex flex-col gap-4">
            <div className="flex items-start gap-3 rounded-lg bg-background-100 border border-background-200/70 p-4">
              <span className="w-10 h-10 shrink-0 rounded-md bg-primary-500/12 flex items-center justify-center text-primary-400">
                <i className="ri-mail-line text-lg" />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">用户反馈邮箱</p>
                <p className="mt-0.5 text-sm text-foreground-600">service@hongtao-video.example.com</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg bg-background-100 border border-background-200/70 p-4">
              <span className="w-10 h-10 shrink-0 rounded-md bg-primary-500/12 flex items-center justify-center text-primary-400">
                <i className="ri-briefcase-line text-lg" />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">商务合作邮箱</p>
                <p className="mt-0.5 text-sm text-foreground-600">partner@hongtao-video.example.com</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg bg-background-100 border border-background-200/70 p-4">
              <span className="w-10 h-10 shrink-0 rounded-md bg-primary-500/12 flex items-center justify-center text-primary-400">
                <i className="ri-time-line text-lg" />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">反馈处理时间</p>
                <p className="mt-0.5 text-sm text-foreground-600">工作日 9:00 - 18:00，我们会在 2 个工作日内回复</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-background-100 border border-background-200/70 p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">在线反馈</h2>
          <p className="mt-1 text-sm text-foreground-600">填写下面的表单，我们会尽快查看你的意见。</p>

          <form
            id="ht-feedback-form"
            data-readdy-form
            action={SUBMIT_ADDR}
            onSubmit={handleSubmit}
            className="mt-5 flex flex-col gap-4"
          >
            <div>
              <label htmlFor="fb-name" className="block text-sm text-foreground-700 mb-1.5">
                您的称呼
              </label>
              <input
                id="fb-name"
                name="name"
                type="text"
                required
                placeholder="请输入您的称呼"
                className="w-full h-11 px-3 rounded-md bg-background-200/50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20 transition"
              />
            </div>

            <div>
              <label htmlFor="fb-email" className="block text-sm text-foreground-700 mb-1.5">
                联系邮箱
              </label>
              <input
                id="fb-email"
                name="email"
                type="email"
                required
                placeholder="请输入您的邮箱"
                className="w-full h-11 px-3 rounded-md bg-background-200/50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20 transition"
              />
            </div>

            <div>
              <label htmlFor="fb-message" className="block text-sm text-foreground-700 mb-1.5">
                反馈内容
              </label>
              <textarea
                id="fb-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请描述您遇到的问题或建议（不超过 500 字）"
                className="w-full px-3 py-2.5 rounded-md bg-background-200/50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20 transition resize-none"
              />
            </div>

            <input
              className="aux-note-field"
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="h-11 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === 'submitting' ? '提交中…' : '提交反馈'}
            </button>

            {status === 'success' && (
              <p className="flex items-center gap-2 text-sm text-accent-400">
                <i className="ri-checkbox-circle-fill" />
                提交成功，感谢你的反馈！我们会尽快处理。
              </p>
            )}
            {status === 'error' && (
              <p className="flex items-center gap-2 text-sm text-primary-400">
                <i className="ri-error-warning-fill" />
                {errorMessage}
              </p>
            )}
          </form>
        </div>
      </section>

      <section className="mt-12 rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
        <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">版权与免责声明</h2>
        <div className="mt-4 flex flex-col gap-3 text-sm text-foreground-600 leading-relaxed">
          <p>
            本网站为演示性质的产品原型，站内所有影片名称、简介、演职人员及封面素材均为虚构示例，仅用于展示页面结构与交互效果，不代表任何真实作品。
          </p>
          <p>
            网站不提供真实的视频播放、下载或会员服务，页面中的播放区域仅为前端演示组件。若你希望接入真实内容源，需要另行对接后端服务。
          </p>
          <p>
            未经许可，请勿将本站演示内容用于商业用途。如涉及任何权益问题，欢迎通过上方联系方式与我们沟通。
          </p>
          <p className="text-foreground-500">
            © 2026 红桃视频 Hongtao Video · 保留所有权利。
          </p>
        </div>
      </section>
    </div>
  );
}